import { useState } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { Badge } from './ui/Badge'
import { useApiMutation } from '../hooks/useApi'
import type { CreateCustomerRequest } from '../types'

interface CustomerCreationWizardProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

interface FormData {
  name: string
  domain: string
  contact_email: string
  region: 'uk' | 'eu' | 'us'
}

const INITIAL_FORM_DATA: FormData = {
  name: '',
  domain: '',
  contact_email: '',
  region: 'uk'
}

const REGIONS = [
  { value: 'uk', label: 'United Kingdom', description: 'GDPR compliant, low latency for UK users' },
  { value: 'eu', label: 'European Union', description: 'GDPR compliant, optimized for EU users' },
  { value: 'us', label: 'United States', description: 'Optimized for US users' },
] as const

const STEPS = [
  { id: 'basic', title: 'Basic Information', description: 'Customer name and contact details' },
  { id: 'region', title: 'Region Selection', description: 'Choose deployment region' },
  { id: 'review', title: 'Review', description: 'Review and confirm customer details' },
] as const

export function CustomerCreationWizard({ isOpen, onClose, onSuccess }: CustomerCreationWizardProps) {
  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const { mutate: createCustomer, loading } = useApiMutation<any, CreateCustomerRequest>()

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear related errors
    const newErrors = { ...errors }
    Object.keys(updates).forEach(key => {
      delete newErrors[key]
    })
    setErrors(newErrors)
  }

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {}

    switch (step) {
      case 0: // Basic Information
        if (!formData.name.trim()) {
          newErrors.name = 'Customer name is required'
        }
        if (!formData.domain.trim()) {
          newErrors.domain = 'Transport app domain is required'
        } else if (!/^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(formData.domain)) {
          newErrors.domain = 'Please enter a valid domain name'
        }
        if (formData.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
          newErrors.contact_email = 'Please enter a valid email address'
        }
        break
      case 1: // Region Selection
        if (!formData.region) {
          newErrors.region = 'Please select a region'
        }
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length - 1))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return

    try {
      await createCustomer('/customers', {
        name: formData.name,
        domain: formData.domain || undefined,
        contact_email: formData.contact_email || undefined,
        region: formData.region
      })

      // Reset form and close
      setFormData(INITIAL_FORM_DATA)
      setCurrentStep(0)
      setErrors({})
      onSuccess()
      onClose()
    } catch (error) {
      setErrors({ submit: error instanceof Error ? error.message : 'Failed to create customer' })
    }
  }

  const handleClose = () => {
    setFormData(INITIAL_FORM_DATA)
    setCurrentStep(0)
    setErrors({})
    onClose()
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basic Information
        return (
          <div className="space-y-4">
            <Input
              label="Customer Name *"
              value={formData.name}
              onChange={(e) => updateFormData({ name: e.target.value })}
              error={errors.name}
              placeholder="Enter customer name"
            />
            
            <Input
              label="Transport App Domain *"
              value={formData.domain}
              onChange={(e) => updateFormData({ domain: e.target.value })}
              error={errors.domain}
              placeholder="acme.redskydev.co.uk"
              helperText="Required: Domain where customers will access their transport app"
            />
            
            <Input
              label="Contact Email"
              type="email"
              value={formData.contact_email}
              onChange={(e) => updateFormData({ contact_email: e.target.value })}
              error={errors.contact_email}
              placeholder="contact@example.com"
              helperText="Optional: Primary contact for this customer"
            />
          </div>
        )

      case 1: // Region Selection
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Select Deployment Region *
              </label>
              <div className="space-y-3">
                {REGIONS.map((region) => (
                  <div
                    key={region.value}
                    className={`relative rounded-lg border p-4 cursor-pointer transition-colors ${
                      formData.region === region.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => updateFormData({ region: region.value })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="region"
                          value={region.value}
                          checked={formData.region === region.value}
                          onChange={() => updateFormData({ region: region.value })}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3">
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-gray-900">
                              {region.label}
                            </span>
                            <Badge variant="info" className="uppercase text-xs">
                              {region.value}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {region.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {errors.region && (
                <p className="mt-2 text-sm text-red-600">{errors.region}</p>
              )}
            </div>
          </div>
        )

      case 2: // Review
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">Review Customer Details</h4>
              
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Name:</span>
                  <span className="text-sm font-medium text-gray-900">{formData.name}</span>
                </div>
                
                {formData.domain && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Domain:</span>
                    <span className="text-sm font-medium text-gray-900">{formData.domain}</span>
                  </div>
                )}
                
                {formData.contact_email && (
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Contact Email:</span>
                    <span className="text-sm font-medium text-gray-900">{formData.contact_email}</span>
                  </div>
                )}
                
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Region:</span>
                  <Badge variant="info" className="uppercase">
                    {formData.region}
                  </Badge>
                </div>
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800">
                    <strong>Next Step:</strong> After creating this customer, you'll be able to deploy their transport app infrastructure with API keys and configuration.
                  </p>
                </div>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}
          </div>
        )

      default:
        return null
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Customer" size="lg">
      <div className="space-y-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                index <= currentStep
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {index + 1}
              </div>
              <div className="ml-2 hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{step.title}</p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>
              {index < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 mx-4 ${
                  index < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 0}
          >
            Previous
          </Button>

          <div className="flex items-center space-x-3">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            
            {currentStep < STEPS.length - 1 ? (
              <Button onClick={nextStep}>
                Next
              </Button>
            ) : (
              <Button onClick={handleSubmit} loading={loading}>
                Create Customer
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}